import { ErrorCode, type ErrorCodeValue } from '../core/errorCodes';
import type { ErrorContext } from '../core/errorContext';
import type { NormalizedApiError } from '../core/normalizedApiError';
import type { UserFacingError } from '../core/userFacingError';
import { parseAxiosApiError } from '../parsing/parseApiError';
import { buildRetryActionIfAllowed, type ActionPolicyInput } from './actionPolicy';
import { copyCatalog, messageForInline, titleForCode } from './copyCatalog.en';
import { generatedEntry } from './catalog.generated';

function labelForAction(kind: UserFacingError['actions'][number]['kind']): string {
  switch (kind) {
    case 'wallet':
      return 'Go to Wallet';
    case 'subscription':
      return 'Subscribe';
    case 'sign_in':
      return 'Sign in';
    case 'retry':
      return 'Retry';
    case 'close':
      return 'Close';
    case 'dismiss':
    default:
      return 'OK';
  }
}

function actionsFromGenerated(code?: string | null): UserFacingError['actions'] {
  const entry = generatedEntry(code);
  if (!entry) return [];
  return entry.actions.map((kind) => ({
    kind,
    label: labelForAction(kind),
  }));
}

function isOffline(error: unknown): boolean {
  const e = error as { message?: string; code?: string };
  const msg = String(e?.message ?? '').toLowerCase();
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  if (msg.includes('network error') || msg.includes('failed to fetch') || e?.code === 'ECONNABORTED') {
    return true;
  }
  return false;
}

export interface MapToUserFacingOptions extends ActionPolicyInput {
  context?: ErrorContext;
}

/**
 * Map transport + parsed API error to UI contract.
 */
export function mapToUserFacingError(
  error: unknown,
  opts: MapToUserFacingOptions = { supportsRetry: false }
): UserFacingError {
  if (isOffline(error)) {
    return {
      surface: 'global_modal',
      title: copyCatalog.titles.offline,
      message: copyCatalog.messages.offline,
      actions: [{ kind: 'dismiss', label: 'OK' }],
      errorCode: 'offline',
    };
  }

  const n = parseAxiosApiError(error) ?? ({} as NormalizedApiError);
  const status = (error as { response?: { status?: number } })?.response?.status;
  const code = n.errorCode;
  const msgBase = n.message || copyCatalog.messages.generic;
  const inlineMsg = messageForInline(code, msgBase);

  if (status === 402 && code === ErrorCode.INSUFFICIENT_WALLET_BALANCE) {
    const generatedActions = actionsFromGenerated(code);
    return {
      surface: 'global_modal',
      title: titleForCode(code, status),
      message: inlineMsg,
      actions:
        generatedActions.length > 0
          ? generatedActions
          : [
              { kind: 'wallet', label: 'Go to Wallet' },
              { kind: 'close', label: 'Close' },
            ],
      errorCode: code,
      suppressInlineDuplicate: true,
    };
  }

  if (status === 429 || code === ErrorCode.RATE_LIMITED) {
    const generatedActions = actionsFromGenerated(code ?? ErrorCode.RATE_LIMITED);
    return {
      surface: 'toast',
      title: titleForCode(code, status),
      message: messageForInline(code ?? ErrorCode.RATE_LIMITED, msgBase),
      actions: generatedActions.length > 0 ? generatedActions : [{ kind: 'dismiss', label: 'OK' }],
      errorCode: code ?? ErrorCode.RATE_LIMITED,
    };
  }

  if (status === 503 || code === ErrorCode.SERVICE_UNAVAILABLE) {
    const generatedActions = actionsFromGenerated(code ?? ErrorCode.SERVICE_UNAVAILABLE);
    return {
      surface: 'global_modal',
      title: titleForCode(code, status),
      message: messageForInline(code ?? ErrorCode.SERVICE_UNAVAILABLE, msgBase),
      actions: generatedActions.length > 0 ? generatedActions : [{ kind: 'dismiss', label: 'OK' }],
      errorCode: code ?? ErrorCode.SERVICE_UNAVAILABLE,
    };
  }

  const authCodes = new Set([
    ErrorCode.TOKEN_INVALID,
    ErrorCode.AUTH_REQUIRED,
    ErrorCode.TOKEN_MISSING,
  ]);
  if (status === 401 || (code != null && authCodes.has(code as ErrorCodeValue))) {
    const generatedActions = actionsFromGenerated(code ?? ErrorCode.AUTH_REQUIRED);
    return {
      surface: 'global_modal',
      title: titleForCode(code, status),
      message: inlineMsg,
      actions:
        generatedActions.length > 0
          ? generatedActions
          : [
              { kind: 'sign_in', label: 'Sign in' },
              { kind: 'dismiss', label: 'OK' },
            ],
      errorCode: code,
      suppressInlineDuplicate: true,
    };
  }

  if (status === 403 && code === ErrorCode.INSUFFICIENT_SCOPE) {
    return {
      surface: 'global_modal',
      title: titleForCode(code, status),
      message: inlineMsg,
      actions: [{ kind: 'dismiss', label: 'OK' }],
      errorCode: code,
    };
  }

  if (status === 402 && code === ErrorCode.SUBSCRIPTION_REQUIRED) {
    const generatedActions = actionsFromGenerated(code);
    return {
      surface: 'alert',
      title: titleForCode(code, status),
      message: inlineMsg,
      actions:
        generatedActions.length > 0
          ? generatedActions
          : [
              { kind: 'subscription', label: 'Subscribe' },
              { kind: 'dismiss', label: 'OK' },
            ],
      errorCode: code,
    };
  }
  const retry = buildRetryActionIfAllowed({
    supportsRetry: opts.supportsRetry,
    errorCode: code,
    httpStatus: status,
  });

  const actions: UserFacingError['actions'] = [];
  if (retry) actions.push(retry);
  actions.push({ kind: 'dismiss', label: 'OK' });

  return {
    surface: 'inline_card',
    title: titleForCode(code, status),
    message: inlineMsg,
    actions,
    errorCode: code,
  };
}
